import { useState, useEffect } from 'react';

interface ShowreelSectionProps {
  title: string;
  subtitle: string;
  description: string;
  ctaLabel: string;
  closeHint?: string;
}

// Dropbox iframe embed URL — uses Dropbox's own video player, most reliable
const EMBED_URL =
  'https://www.dropbox.com/scl/fi/lernazrc4fghd2b2tlyxh/Huawei.mp4?rlkey=fkhycgupq26a42vrr5bok0g9m&st=qke73tzf&raw=1';

export default function ShowreelSection({
  title,
  subtitle,
  description,
  ctaLabel,
  closeHint = 'Press ESC or click outside to close',
}: ShowreelSectionProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  const handleClose = () => {
    setIsOpen(false);
    setIframeLoaded(false);
  };

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    if (isOpen) document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  return (
    <>
      <section
        className="relative py-32 overflow-hidden"
        style={{
          backgroundImage:
            "url('https://readdy.ai/api/search-image?query=dramatic%20large%20concert%20stage%20with%20professional%20lighting%20rigs%20LED%20screens%20crowd%20silhouettes%20dark%20atmospheric%20event%20production%20cinematic%20wide%20angle%20shot%20deep%20blacks%20golden%20amber%20spotlights%20smoke%20haze%20epic%20scale&width=1920&height=800&seq=showreel-bg-001&orientation=landscape')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
        }}
      >
        <div className="absolute inset-0 bg-black/70"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/60"></div>

        <div className="relative z-10 flex flex-col items-center justify-center text-center px-6">
          <p className="text-yellow-400 text-xs font-bold uppercase tracking-[0.3em] mb-4">{subtitle}</p>
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">{title}</h2>
          <p className="text-gray-300 text-lg max-w-2xl mb-12 leading-relaxed">{description}</p>

          <button
            onClick={() => setIsOpen(true)}
            className="group relative flex items-center justify-center cursor-pointer"
            aria-label={ctaLabel}
          >
            <span className="absolute w-28 h-28 rounded-full border border-yellow-400/30 animate-ping"></span>
            <span className="absolute w-24 h-24 rounded-full border border-yellow-400/20 animate-ping" style={{ animationDelay: '0.3s' }}></span>
            <span className="relative w-20 h-20 flex items-center justify-center rounded-full bg-yellow-400 group-hover:bg-yellow-300 transition-all duration-300 group-hover:scale-110">
              <i className="ri-play-fill text-3xl text-black ml-1"></i>
            </span>
          </button>
          <p className="text-gray-400 text-sm mt-6 font-medium">{ctaLabel}</p>
        </div>
      </section>

      {/* Video Modal */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/95 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
          onClick={handleClose}
        >
          <div
            className="relative w-full max-w-5xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={handleClose}
              className="absolute -top-12 right-0 w-10 h-10 flex items-center justify-center text-white hover:text-yellow-400 transition-colors cursor-pointer z-10"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>

            {/* Video container */}
            <div className="relative w-full rounded-xl overflow-hidden bg-black" style={{ paddingBottom: '56.25%' }}>

              {/* Loading spinner — shown until iframe loads */}
              {!iframeLoaded && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-black z-10">
                  <div className="w-14 h-14 border-4 border-yellow-400/30 border-t-yellow-400 rounded-full animate-spin mb-4"></div>
                  <p className="text-white text-sm font-medium">Video yükleniyor...</p>
                  <p className="text-gray-500 text-xs mt-2">Bu birkaç saniye sürebilir</p>
                </div>
              )}

              <iframe
                className="absolute inset-0 w-full h-full"
                src={EMBED_URL}
                allow="autoplay; fullscreen"
                allowFullScreen
                onLoad={() => setIframeLoaded(true)}
                style={{ border: 'none' }}
                title="Showreel Video"
              />
            </div>

            {/* Info bar */}
            <div className="mt-3 px-1">
              <p className="text-gray-500 text-xs">{closeHint}</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
